/**
 * Включение/выключение модуля (ARCH-TASK-65.12.7).
 *
 * Вынесено из `ModuleCard`, чтобы логику «meta + applyEnabled» можно было
 * покрыть тестом без рендера React.
 *
 * Флаги модулей — tenant-level настройка (`tenants.settings.modules`), но
 * касса должна переключать модуль и офлайн. Поэтому локальная запись — это
 * durable-очередь: она переживает перезагрузку и повторяется при связи.
 */
import type { AppCore } from '@core/bootstrap'
import { PosEvent } from '@core/bus/events'
import type { PosDatabase } from '@core/db/db'
import { META_MODULES_PENDING } from '@core/db/schema'
import type { ModuleUiDescriptor } from './types'

export interface ModuleEnableDeps {
  /** `receipts` нужен для защиты от выключения ЧЗ при незавершённых hold-чеках. */
  db: Pick<PosDatabase, 'meta'> & Partial<Pick<PosDatabase, 'receipts'>>
  descriptor: ModuleUiDescriptor
  core: AppCore
}

const MODULES_SETTING_KEY = 'modules'
const RETRY_DELAYS_MS = [5_000, 15_000, 30_000, 60_000]

interface RemoteSettings {
  settings?: { modules?: unknown }
}

export interface PendingModuleFlag {
  enabled: boolean
  /** Версия локального намерения: защищает от затирания более новым pull. */
  updatedAt: number
}

interface PendingModules {
  flags: Record<string, PendingModuleFlag>
  failures?: number
  retryAt?: number
  lastAttemptAt?: number
  lastError?: string
}

export interface ModuleSyncResult {
  synced: boolean
  /** Флаг остался в очереди и будет повторён. */
  pending: boolean
  error?: string
}

/**
 * Сериализация read-modify-write очереди. В одном окне это защищает от
 * гонки двух быстрых переключений, а IndexedDB хранит результат между
 * перезагрузками.
 */
let pendingLock: Promise<unknown> = Promise.resolve()

function withPendingLock<T>(fn: () => Promise<T>): Promise<T> {
  const run = pendingLock.then(fn, fn)
  pendingLock = run.then(
    () => undefined,
    () => undefined
  )
  return run
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function normalizePending(raw: unknown): PendingModules {
  if (!isRecord(raw) || !isRecord(raw.flags)) return { flags: {} }
  const flags: Record<string, PendingModuleFlag> = {}
  for (const [id, value] of Object.entries(raw.flags)) {
    if (!isRecord(value)) continue
    if (typeof value.enabled !== 'boolean') continue
    if (typeof value.updatedAt !== 'number' || !Number.isFinite(value.updatedAt)) continue
    flags[id] = { enabled: value.enabled, updatedAt: value.updatedAt }
  }
  return {
    flags,
    failures: typeof raw.failures === 'number' ? raw.failures : undefined,
    retryAt: typeof raw.retryAt === 'number' ? raw.retryAt : undefined,
    lastAttemptAt: typeof raw.lastAttemptAt === 'number' ? raw.lastAttemptAt : undefined,
    lastError: typeof raw.lastError === 'string' ? raw.lastError : undefined
  }
}

async function readPending(db: Pick<PosDatabase, 'meta'>): Promise<PendingModules> {
  const entry = await db.meta.get(META_MODULES_PENDING)
  return normalizePending(entry?.value)
}

async function writePending(
  db: Pick<PosDatabase, 'meta'>,
  pending: PendingModules
): Promise<void> {
  await db.meta.put({ key: META_MODULES_PENDING, value: pending })
}

async function enqueuePending(
  db: Pick<PosDatabase, 'meta'>,
  moduleId: string,
  enabled: boolean
): Promise<void> {
  await withPendingLock(async () => {
    const pending = await readPending(db)
    const previous = pending.flags[moduleId]?.updatedAt ?? 0
    pending.flags[moduleId] = {
      enabled,
      updatedAt: Math.max(Date.now(), previous + 1)
    }
    pending.failures = 0
    delete pending.retryAt
    delete pending.lastError
    await writePending(db, pending)
  })
}

/**
 * Удалить только тот снимок, который реально ушёл в облако. Если кассир
 * успел переключить модуль ещё раз, более новая запись останется в очереди.
 */
async function clearPendingSnapshot(
  db: Pick<PosDatabase, 'meta'>,
  snapshot: Record<string, PendingModuleFlag>
): Promise<void> {
  await withPendingLock(async () => {
    const pending = await readPending(db)
    for (const [id, sent] of Object.entries(snapshot)) {
      const current = pending.flags[id]
      if (current && current.updatedAt === sent.updatedAt && current.enabled === sent.enabled) {
        delete pending.flags[id]
      }
    }
    if (Object.keys(pending.flags).length === 0) {
      pending.failures = 0
      delete pending.retryAt
      delete pending.lastError
      delete pending.lastAttemptAt
    }
    await writePending(db, pending)
  })
}

async function markPendingFailure(
  db: Pick<PosDatabase, 'meta'>,
  error: string
): Promise<void> {
  await withPendingLock(async () => {
    const pending = await readPending(db)
    const failures = (pending.failures ?? 0) + 1
    pending.failures = failures
    pending.retryAt =
      Date.now() + RETRY_DELAYS_MS[Math.min(failures - 1, RETRY_DELAYS_MS.length - 1)]
    pending.lastAttemptAt = Date.now()
    pending.lastError = error
    await writePending(db, pending)
  })
}

/** Вернуть pending-флаг конкретного модуля (для UI и pull). */
export async function getPendingModuleFlag(
  db: Pick<PosDatabase, 'meta'>,
  moduleId: string
): Promise<PendingModuleFlag | null> {
  const pending = await readPending(db)
  return pending.flags[moduleId] ?? null
}

/** Прочитать tenant-level флаги. Нет облака/токена — работаем локально. */
async function readRemoteModuleFlags(core: AppCore): Promise<Record<string, boolean> | null> {
  try {
    const response = await core.cloud.request('/tenant/settings')
    if (response.status < 200 || response.status >= 300) return null
    const body = response.body as RemoteSettings | null
    const raw = body?.settings?.modules
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
    const flags: Record<string, boolean> = {}
    for (const [id, value] of Object.entries(raw)) {
      if (typeof value === 'boolean') flags[id] = value
    }
    return flags
  } catch {
    return null
  }
}

/**
 * Отправить очередь. Delta (`{ modules: { id: enabled } }`) специально:
 * backend смёржит её с текущими флагами. Полная карта могла бы затереть
 * изменение, сделанное на другом терминале между GET и PUT.
 */
export async function flushPendingModuleFlags(
  deps: { db: Pick<PosDatabase, 'meta'>; core: AppCore },
  options: { force?: boolean } = {}
): Promise<ModuleSyncResult> {
  const pending = await readPending(deps.db)
  const ids = Object.keys(pending.flags)
  if (ids.length === 0) return { synced: true, pending: false }
  if (!options.force && pending.retryAt != null && pending.retryAt > Date.now()) {
    return { synced: false, pending: true, error: pending.lastError }
  }

  const snapshot: Record<string, PendingModuleFlag> = {}
  const flags: Record<string, boolean> = {}
  for (const id of ids) {
    snapshot[id] = { ...pending.flags[id] }
    flags[id] = pending.flags[id].enabled
  }

  if (!deps.core.cloud) {
    await markPendingFailure(deps.db, 'not_connected')
    return { synced: false, pending: true, error: 'not_connected' }
  }

  try {
    const response = await deps.core.cloud.request('/tenant/settings', {
      method: 'PUT',
      body: { [MODULES_SETTING_KEY]: flags }
    })
    if (response.status >= 200 && response.status < 300) {
      await clearPendingSnapshot(deps.db, snapshot)
      return { synced: true, pending: false }
    }

    const error = `HTTP ${response.status}`
    // Ошибка формата не станет успешнее от повтора: снимаем только этот снимок.
    if (response.status === 400 || response.status === 422) {
      await clearPendingSnapshot(deps.db, snapshot)
      return { synced: false, pending: false, error }
    }
    await markPendingFailure(deps.db, error)
    return { synced: false, pending: true, error }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    await markPendingFailure(deps.db, message)
    return { synced: false, pending: true, error: message }
  }
}

async function notifyIfChanged(
  deps: ModuleEnableDeps,
  before: boolean,
  pending: boolean
): Promise<void> {
  const after = deps.descriptor.isEnabled?.(deps.core)
  if (after == null || after === before) return
  await deps.core.bus.emit(PosEvent.MODULE_ENABLED_CHANGED, {
    id: deps.descriptor.id,
    enabled: after,
    pending
  })
}

/**
 * Прочитать состояние модуля.
 *
 * Приоритет:
 * 1. pending-флаг (локальное намерение ещё не ушло в облако);
 * 2. серверный флаг;
 * 3. локальный кэш.
 */
export async function loadModuleEnabled(
  deps: ModuleEnableDeps,
  remoteFlags?: Record<string, boolean> | null
): Promise<boolean> {
  const before = deps.descriptor.isEnabled?.(deps.core) ?? false
  const pending = await getPendingModuleFlag(deps.db, deps.descriptor.id)
  if (pending) {
    deps.descriptor.applyEnabled?.(pending.enabled, deps.core)
    await deps.db.meta.put({ key: deps.descriptor.enabledMetaKey, value: pending.enabled })
    await notifyIfChanged(deps, before, true)
    return pending.enabled
  }

  const remote =
    remoteFlags !== undefined ? remoteFlags : await readRemoteModuleFlags(deps.core)
  if (remote && typeof remote[deps.descriptor.id] === 'boolean') {
    const enabled = remote[deps.descriptor.id]
    deps.descriptor.applyEnabled?.(enabled, deps.core)
    await deps.db.meta.put({ key: deps.descriptor.enabledMetaKey, value: enabled })
    await notifyIfChanged(deps, before, false)
    return enabled
  }

  const entry = await deps.db.meta.get(deps.descriptor.enabledMetaKey)
  const enabled = typeof entry?.value === 'boolean' ? entry.value : true
  deps.descriptor.applyEnabled?.(enabled, deps.core)
  await notifyIfChanged(deps, before, false)
  return enabled
}

/**
 * Сохранить состояние: сначала локально и в durable-очередь, затем отправить.
 * Если облако недоступно — модуль всё равно переключается локально,
 * а вызывающий UI показывает «ожидает синхронизации».
 */
export async function setModuleEnabled(
  deps: ModuleEnableDeps,
  enabled: boolean
): Promise<ModuleSyncResult> {
  const before = deps.descriptor.isEnabled?.(deps.core) ?? false

  if (!enabled && deps.descriptor.canDisable) {
    const reason = await deps.descriptor.canDisable(deps.core)
    if (reason) throw new Error(reason)
  }

  const previousEntry = await deps.db.meta.get(deps.descriptor.enabledMetaKey)
  const previous = typeof previousEntry?.value === 'boolean' ? previousEntry.value : true

  deps.descriptor.applyEnabled?.(enabled, deps.core)
  try {
    await deps.db.meta.put({ key: deps.descriptor.enabledMetaKey, value: enabled })
    await enqueuePending(deps.db, deps.descriptor.id, enabled)
  } catch (error) {
    // IndexedDB недоступна — не оставляем UI в состоянии, которого нет на диске.
    await deps.db.meta.put({ key: deps.descriptor.enabledMetaKey, value: previous }).catch(
      () => undefined
    )
    deps.descriptor.applyEnabled?.(previous, deps.core)
    throw error
  }
  await notifyIfChanged(deps, before, true)
  return flushPendingModuleFlags({ db: deps.db, core: deps.core }, { force: true })
}

/** Откатить применение, если локальная запись в meta не удалась. */
export function rollbackModuleEnabled(deps: ModuleEnableDeps, previous: boolean): void {
  deps.descriptor.applyEnabled?.(previous, deps.core)
}

/**
 * Синхронизировать все зарегистрированные модули с tenant settings.
 * Один GET на цикл: иначе каждый модуль делает собственный запрос.
 */
export async function syncAllModuleStates(
  db: Pick<PosDatabase, 'meta'>,
  descriptors: ModuleUiDescriptor[],
  core: AppCore
): Promise<void> {
  await flushPendingModuleFlags({ db, core })
  const remote = await readRemoteModuleFlags(core)
  await Promise.all(
    descriptors.map((descriptor) => loadModuleEnabled({ db, descriptor, core }, remote))
  )
}
